import { useState } from "react";
import { api } from "@/lib/api";
import type { ReportParams } from "./types";

export type ReportType = "water" | "heat" | "electricity";

async function triggerDownload(
	endpoint: string,
	params: ReportParams,
): Promise<void> {
	const res = await api.get(endpoint, { params, responseType: "blob" });
	const url = URL.createObjectURL(res.data as Blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = "report.csv";
	const disposition = res.headers["content-disposition"] as string | undefined;
	if (disposition) {
		const match = disposition.match(/filename="?([^";\n]+)"?/);
		if (match?.[1]) a.download = match[1];
	}
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	URL.revokeObjectURL(url);
}

function downloadWaterReport(params: ReportParams): Promise<void> {
	return triggerDownload("/api/districts/water_report/", params);
}

function downloadHeatReport(params: ReportParams): Promise<void> {
	return triggerDownload("/api/districts/heat_report/", params);
}

function downloadElectricityReport(params: ReportParams): Promise<void> {
	return triggerDownload("/api/districts/electricity_report/", params);
}

const reportDownloaders: Record<
	ReportType,
	(params: ReportParams) => Promise<void>
> = {
	water: downloadWaterReport,
	heat: downloadHeatReport,
	electricity: downloadElectricityReport,
};

export function useGenerateReport() {
	const [isLoading, setIsLoading] = useState(false);

	async function generate(
		types: ReportType[],
		params: ReportParams,
	): Promise<void> {
		setIsLoading(true);
		try {
			await Promise.all(types.map((type) => reportDownloaders[type](params)));
		} finally {
			setIsLoading(false);
		}
	}

	return { generate, isLoading };
}
